import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setAmount } from 'redux/actions/orders';
import { Box, Text } from 'components/Wrapped';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { useFormikContext } from 'formik';

const CreatePercentSelectors = ({ setAmount, side, marketOrder, tradingPair, portfolios, total, amount, lastPrice }) => {
  const { validateForm } = useFormikContext();
  const [selected, setSelected] = useState(0);
  const balance = _.get(portfolios, `${side === 'BUY' ? tradingPair.quoteCurrency : tradingPair.baseCurrency}.balance`, 0);

  useEffect(() => {
    if (total && side === 'BUY' && balance) {
      const result = Number(((total.replace(/\s/g, '') / balance) * 100).toFixed(2)) || 0;

      setSelected(_.clamp(result, 0, 100));
    }
  }, [total, side, balance]);

  useEffect(() => {
    if (amount && side === 'SELL' && balance) {
      setSelected(Number(((amount / balance) * 100).toFixed(2)));
    }

    if (amount && side === 'BUY' && marketOrder && balance) {
      const total = lastPrice * amount;

      setSelected(Number(((total / balance) * 100).toFixed(2)));
    }
  }, [amount, lastPrice, balance, marketOrder, side]);

  const percentSelectorMark = (value) => {
    return (
      <Box
        key={value}
        background={value === selected ? 'background' : null}
        pad="none"
        onClick={() => {
          handleChange(value);
        }}
        round="medium"
        margin={{ vertical: 'xsmall', left: '4px' }}
        tabIndex={-1}
      >
        <Text size="10px" weight={500} color={value === selected ? 'white' : '#B4B6B7'} fontFamily="alt">
          {value}%
        </Text>
      </Box>
    );
  };

  const marks = {
    0: {
      label: percentSelectorMark(selected),
    },
    100: {
      label: percentSelectorMark(100),
    },
  };

  const percentFormatter = (value) => {
    return `${value}%`;
  };

  const handleChange = _.debounce((value) => {
    setSelected(value);
    setAmount(side, value / 100, marketOrder);
    validateForm();
  }, 250);

  const handleDrag = (value) => {
    setSelected(value);
  };

  return (
    <Box pad={{ horizontal: '18px' }} gap="small" margin={{ top: 'small', bottom: '35px' }}>
      <Slider
        min={0}
        max={100}
        marks={marks}
        value={selected}
        tipFormatter={percentFormatter}
        trackStyle={{ backgroundColor: '#568677', height: 3 }}
        railStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', height: 3 }}
        dotStyle={{
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          height: 12,
          width: 12,
          bottom: -6,
        }}
        handleStyle={{
          marginTop: -6,
          height: 15,
          width: 15,
          borderColor: 'var(--handleColor)',
          backgroundColor: 'var(--handleColor)',
          boxShadow: 'none',
        }}
        activeDotStyle={{
          backgroundColor: 'transparent',
          borderColor: 'transparent',
        }}
        onChange={handleDrag}
        onAfterChange={handleChange}
        tabIndex={-1}
      />
    </Box>
  );
};

CreatePercentSelectors.propTypes = {
  setAmount: PropTypes.func.isRequired,
  side: PropTypes.string.isRequired,
  marketOrder: PropTypes.bool.isRequired,
};

CreatePercentSelectors.defaultProps = {
  marketOrder: false,
};

const mapStateToProps = ({
  portfolio,
  exchange: {
    tradingPair,
    tradingPairStats: { lastPrice },
  },
}) => ({
  tradingPair,
  portfolios: portfolio.portfolios,
  lastPrice,
});

export default connect(mapStateToProps, { setAmount })(CreatePercentSelectors);
